import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  FileText, Send, Clock, CheckCircle2, XCircle, AlertCircle, Eye, EyeOff,
  Download, Copy, Loader2, ShieldCheck, ChevronRight, Upload, AlertTriangle, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTinDisplay = (raw, kind) => {
  if (!raw) return '';
  const d = String(raw).replace(/\D/g, '');
  if (kind === 'ein') {
    if (d.length <= 2) return d;
    return `${d.slice(0, 2)}-${d.slice(2)}`;
  }
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

const STATUS_PILL = {
  not_sent:   { cls: 'bg-slate-100 text-slate-700',   icon: Clock,         label: 'Not sent' },
  sent:       { cls: 'bg-blue-100 text-blue-700',     icon: Send,          label: 'Sent' },
  in_progress:{ cls: 'bg-amber-100 text-amber-700',   icon: Clock,         label: 'In progress' },
  submitted:  { cls: 'bg-violet-100 text-violet-700', icon: AlertCircle,   label: 'Submitted' },
  approved:   { cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2,label: 'Approved' },
  rejected:   { cls: 'bg-red-100 text-red-700',       icon: XCircle,       label: 'Rejected' },
  expired:    { cls: 'bg-slate-100 text-slate-500',   icon: XCircle,       label: 'Expired' },
};

const CONTRACTOR_KINDS = [
  { v: 'owner_operator', l: 'Owner Operator' },
  { v: 'independent_contractor', l: 'Independent Contractor' },
  { v: 'dispatch_contractor', l: 'Dispatch Contractor' },
  { v: 'maintenance_vendor', l: 'Maintenance Vendor' },
  { v: 'factoring_partner', l: 'Factoring Partner' },
  { v: 'other', l: 'Other Service Provider' },
];

const W9AdminPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    contractor_name: '', contractor_email: '', contractor_kind: 'owner_operator', expires_in_days: 14,
  });
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [latestLink, setLatestLink] = useState(null);
  const [showManualUpload, setShowManualUpload] = useState(false);
  const [expiringSoon, setExpiringSoon] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/w9/requests`, { withCredentials: true });
      setItems(res.data.items || []);
    } catch (e) {
      console.error('[W9] list failed', e);
      toast.error('Failed to load W-9 records');
    } finally { setLoading(false); }
  }, []);

  const loadExpiringSoon = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/w9/admin/expiring-soon`, { withCredentials: true });
      setExpiringSoon(res.data);
    } catch (e) {
      // best-effort widget; don't toast
    }
  }, []);

  useEffect(() => { load(); loadExpiringSoon(); }, [load, loadExpiringSoon]);

  const runScan = async () => {
    setScanBusy(true);
    try {
      const res = await axios.post(`${API}/w9/admin/scan-expirations`, {}, { withCredentials: true });
      toast.success(`Scan complete — ${res.data.expired} expired, ${res.data.reminders_sent} reminders sent`);
      await loadExpiringSoon();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    } finally {
      setScanBusy(false);
    }
  };

  const loadDetail = useCallback(async (id, withReveal = false) => {
    try {
      const res = await axios.get(`${API}/w9/requests/${id}?reveal=${withReveal}`, { withCredentials: true });
      setDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load detail');
    }
  }, []);

  const onOpenDetail = async (id) => {
    setSelectedId(id); setReveal(false); setRejectMode(false); setRejectReason('');
    await loadDetail(id, false);
  };

  const onToggleReveal = async () => {
    const next = !reveal;
    setReveal(next);
    if (selectedId) await loadDetail(selectedId, next);
  };

  const onSendRequest = async () => {
    if (!form.contractor_email || !form.contractor_name) {
      toast.error('Email and name are required'); return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/w9/requests`, form, { withCredentials: true });
      toast.success('W-9 request sent');
      setLatestLink(res.data.link);
      setShowForm(false);
      setForm({ contractor_name: '', contractor_email: '', contractor_kind: 'owner_operator', expires_in_days: 14 });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    } finally { setBusy(false); }
  };

  const onApprove = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/w9/requests/${selectedId}/approve`, { note: '' }, { withCredentials: true });
      toast.success('W-9 approved');
      await load(); await loadDetail(selectedId, reveal);
    } catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
    finally { setBusy(false); }
  };

  const onReject = async () => {
    if (!rejectReason.trim()) { toast.error('Rejection reason required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/w9/requests/${selectedId}/reject`, { note: rejectReason.trim() }, { withCredentials: true });
      toast.success('W-9 rejected — contractor notified with a new link');
      setRejectMode(false); setRejectReason('');
      await load(); await loadDetail(selectedId, reveal);
    } catch (e) { toast.error(e.response?.data?.detail || 'Rejection failed'); }
    finally { setBusy(false); }
  };

  const downloadPdf = async (formId, name = 'W9') => {
    try {
      const res = await axios.get(`${API}/w9/forms/${formId}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `W9-${name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('PDF download failed'); }
  };

  const filtered = items.filter((i) => filter === 'all' || i.status === filter);
  const counts = items.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="w9-admin-page">
        <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              W-9 Forms
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Request, review, and securely store IRS Form W-9 from contractors. Taxpayer IDs are encrypted at rest and audit-logged on every access.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runScan}
              disabled={scanBusy}
              data-testid="w9-run-expiration-scan"
              title="Run IRS 3-year expiration scan and send re-collection reminders"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-md font-semibold text-sm transition-colors disabled:opacity-60"
            >
              {scanBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Run expiration scan
            </button>
            <button
              onClick={() => { setShowManualUpload(true); setShowForm(false); setLatestLink(null); }}
              data-testid="w9-manual-upload-button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-md font-semibold text-sm transition-colors"
            >
              <Upload className="w-4 h-4" /> Manual Upload
            </button>
            <button
              onClick={() => { setShowForm(!showForm); setShowManualUpload(false); setLatestLink(null); }}
              data-testid="w9-send-button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] hover:bg-[#00247D] text-white rounded-md font-semibold text-sm transition-colors"
            >
              <Send className="w-4 h-4" /> {showForm ? 'Close' : 'Send W-9 Request'}
            </button>
          </div>
        </header>

        {expiringSoon && (expiringSoon.count > 0 || expiringSoon.expired_count > 0) && (
          <div
            data-testid="w9-expiring-banner"
            className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-5 flex items-start justify-between gap-3"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <div className="flex items-start gap-3 min-w-0">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-amber-900">W-9 Re-collection Reminder</p>
                <p className="text-xs text-amber-800 mt-0.5">
                  {expiringSoon.count > 0 && (
                    <>
                      <strong>{expiringSoon.count}</strong> W-9{expiringSoon.count > 1 ? 's' : ''} expiring within{' '}
                      {expiringSoon.remind_window_days} days
                    </>
                  )}
                  {expiringSoon.count > 0 && expiringSoon.expired_count > 0 && ' • '}
                  {expiringSoon.expired_count > 0 && (
                    <>
                      <strong>{expiringSoon.expired_count}</strong> already expired (IRS 3-year rule)
                    </>
                  )}
                  . Send a refresh request before the next payment cycle.
                </p>
                {expiringSoon.items?.length > 0 && (
                  <ul className="mt-2 text-xs text-amber-900 space-y-0.5">
                    {expiringSoon.items.slice(0, 3).map((it) => (
                      <li key={it.form_id}>
                        <span className="font-semibold">{it.legal_name}</span>
                        <span className="text-amber-700"> · {it.days_left}d left</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {showManualUpload && (
          <ManualUploadCard
            onClose={() => setShowManualUpload(false)}
            onUploaded={() => { setShowManualUpload(false); load(); }}
          />
        )}

        {showForm && (
          <div className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid="w9-send-form" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Contractor Name *">
                <input
                  value={form.contractor_name}
                  onChange={(e) => setForm({ ...form, contractor_name: e.target.value })}
                  data-testid="w9-name-input"
                  placeholder="John Independent"
                  className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/40"
                />
              </Field>
              <Field label="Contractor Email *">
                <input
                  type="email" value={form.contractor_email}
                  onChange={(e) => setForm({ ...form, contractor_email: e.target.value })}
                  data-testid="w9-email-input"
                  placeholder="contractor@example.com"
                  className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/40"
                />
              </Field>
              <Field label="Contractor Type">
                <select
                  value={form.contractor_kind}
                  onChange={(e) => setForm({ ...form, contractor_kind: e.target.value })}
                  data-testid="w9-kind-select"
                  className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white"
                >
                  {CONTRACTOR_KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
                </select>
              </Field>
              <Field label="Link Expires In (days)">
                <input
                  type="number" min={1} max={90} value={form.expires_in_days}
                  onChange={(e) => setForm({ ...form, expires_in_days: parseInt(e.target.value, 10) || 14 })}
                  data-testid="w9-expires-input"
                  className="w-full text-sm border border-slate-300 rounded-md px-3 py-2"
                />
              </Field>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={onSendRequest} disabled={busy}
                data-testid="w9-submit-send"
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold text-sm disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send Request
              </button>
            </div>
          </div>
        )}

        {latestLink && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6 flex items-start justify-between gap-3" data-testid="w9-latest-link">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-widest text-blue-800 mb-1">Secure link generated</p>
              <p className="text-xs text-blue-700 font-mono truncate">{latestLink}</p>
              <p className="text-[11px] text-blue-600 mt-1">An email has been sent if the contractor&apos;s email service accepts it. You can also share this link directly.</p>
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(latestLink); toast.success('Link copied'); }}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded text-xs font-semibold hover:bg-blue-100 flex-shrink-0"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
        )}

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {['all', 'sent', 'in_progress', 'submitted', 'approved', 'rejected', 'expired'].map((k) => {
            const cnt = k === 'all' ? items.length : (counts[k] || 0);
            const active = filter === k;
            const meta = k === 'all' ? { label: 'All' } : STATUS_PILL[k];
            return (
              <button
                key={k} onClick={() => setFilter(k)}
                data-testid={`w9-filter-${k}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors flex items-center gap-2 ${active ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'}`}
              >
                {meta.label}
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-white/30' : 'bg-slate-100 text-slate-700'}`}>{cnt}</span>
              </button>
            );
          })}
        </div>

        {/* List + detail */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3" data-testid="w9-list">
            {loading ? (
              <div className="bg-white border border-slate-200 rounded-md p-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center">
                <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No W-9 requests yet. Click <strong>Send W-9 Request</strong> above to get started.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {filtered.map((r) => {
                  const meta = STATUS_PILL[r.status] || STATUS_PILL.not_sent;
                  const Icon = meta.icon;
                  const isSelected = selectedId === r.id;
                  return (
                    <li key={r.id} data-testid={`w9-row-${r.id}`}>
                      <button
                        onClick={() => onOpenDetail(r.id)}
                        className={`w-full text-left bg-white border rounded-md p-3 flex items-center justify-between gap-3 transition-colors ${isSelected ? 'border-[#002FA7] ring-2 ring-[#002FA7]/20' : 'border-slate-200 hover:border-slate-300'}`}
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-900 truncate text-sm">{r.contractor_name}</p>
                          <p className="text-xs text-slate-500 truncate">{r.contractor_email}</p>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest flex-shrink-0 ${meta.cls}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="lg:col-span-2 lg:sticky lg:top-24 self-start">
            {!detail ? (
              <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-8 text-center text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Select a request to review submitted information.
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="w9-detail" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <div className="flex items-start justify-between gap-2 mb-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Contractor</p>
                    <p className="font-bold text-slate-900">{detail.contractor_name}</p>
                    <p className="text-xs text-slate-500">{detail.contractor_email}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest ${(STATUS_PILL[detail.status] || STATUS_PILL.not_sent).cls}`}>{(STATUS_PILL[detail.status] || STATUS_PILL.not_sent).label}</span>
                </div>

                {!detail.form ? (
                  <p className="text-xs text-slate-500 italic">Form has not been submitted yet. Waiting on contractor.</p>
                ) : (
                  <>
                    <DetailRow label="Legal Name" value={detail.form.legal_name} />
                    <DetailRow label="Business Name" value={detail.form.business_name || '—'} />
                    <DetailRow label="Tax Classification" value={detail.form.tax_classification?.replaceAll('_', ' ')} />
                    <DetailRow label="Address" value={`${detail.form.address}, ${detail.form.city}, ${detail.form.state} ${detail.form.zip}`} />
                    <div className="py-2 border-b border-slate-100">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{detail.form.taxpayer_id_kind?.toUpperCase()}</p>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <p className="font-mono text-sm text-slate-900" data-testid="w9-tin-display">
                          {reveal && detail.form.taxpayer_id_full
                            ? formatTinDisplay(detail.form.taxpayer_id_full, detail.form.taxpayer_id_kind)
                            : detail.form.taxpayer_id_masked}
                        </p>
                        <button
                          onClick={onToggleReveal}
                          data-testid="w9-reveal-toggle"
                          className="inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline font-semibold"
                        >
                          {reveal ? <><EyeOff className="w-3 h-3" /> Hide</> : <><Eye className="w-3 h-3" /> Reveal</>}
                        </button>
                      </div>
                      {reveal && (
                        <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                          <ShieldCheck className="w-3 h-3" /> This view has been audit-logged.
                        </p>
                      )}
                    </div>
                    <DetailRow label="Signature" value={detail.form.signature} italic />
                    <DetailRow label="Signed at" value={detail.form.signed_at ? new Date(detail.form.signed_at).toLocaleString() : '—'} />

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() => downloadPdf(detail.form.id, detail.form.legal_name)}
                        data-testid="w9-download-pdf"
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 hover:border-slate-400 text-slate-700 rounded text-xs font-semibold"
                      >
                        <Download className="w-3 h-3" /> Download PDF
                      </button>
                      {detail.status === 'submitted' && (
                        <>
                          <button
                            onClick={() => setRejectMode(true)}
                            data-testid="w9-reject"
                            className="inline-flex items-center gap-1 px-3 py-1.5 border border-red-300 hover:bg-red-50 text-red-700 rounded text-xs font-semibold"
                          ><XCircle className="w-3 h-3" /> Reject</button>
                          <button
                            onClick={onApprove} disabled={busy}
                            data-testid="w9-approve"
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold disabled:opacity-60"
                          ><CheckCircle2 className="w-3 h-3" /> Approve</button>
                        </>
                      )}
                    </div>

                    {rejectMode && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                        <label className="block text-[10px] font-bold uppercase tracking-widest text-red-700 mb-1">Rejection Reason (sent to contractor)</label>
                        <textarea
                          value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                          data-testid="w9-reject-reason"
                          placeholder="e.g. SSN appears to be missing a digit. Please re-submit with the correct value."
                          className="w-full text-sm border border-red-300 rounded px-2 py-1.5 min-h-[70px]"
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <button onClick={() => setRejectMode(false)} className="px-3 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
                          <button onClick={onReject} disabled={busy || !rejectReason.trim()} data-testid="w9-confirm-reject" className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-semibold disabled:opacity-60">
                            Reject & Notify
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const DetailRow = ({ label, value, italic }) => (
  <div className="py-2 border-b border-slate-100">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`text-sm text-slate-800 mt-0.5 ${italic ? 'italic' : ''}`}>{value || '—'}</p>
  </div>
);


// ── Manual Upload Card — admin attaches a paper W-9 PDF directly ──
const ManualUploadCard = ({ onClose, onUploaded }) => {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    legal_name: '',
    contractor_email: '',
    contractor_kind: 'owner_operator',
    taxpayer_id_kind: 'ssn',
    taxpayer_id: '',
    business_name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    note: '',
  });

  const submit = async () => {
    if (!file) { toast.error('Choose a file first'); return; }
    if (!form.legal_name.trim() || !form.contractor_email.trim()) {
      toast.error('Legal name and email are required'); return;
    }
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
    try {
      setBusy(true);
      await axios.post(`${API}/w9/forms/upload-manual`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('W-9 uploaded successfully');
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid="w9-manual-upload-card" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Manual W-9 Upload</h2>
          <p className="text-xs text-slate-500 mt-1">Upload a contractor&apos;s already-completed W-9 PDF or scan. The form is saved as approved.</p>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="w9-manual-close"><XCircle className="w-4 h-4 text-slate-500" /></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Legal Name *">
          <input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="w9-manual-name" placeholder="John Independent" className="w-full text-sm border border-slate-300 rounded-md px-3 py-2" />
        </Field>
        <Field label="Contractor Email *">
          <input type="email" value={form.contractor_email} onChange={(e) => setForm({ ...form, contractor_email: e.target.value })} data-testid="w9-manual-email" placeholder="contractor@example.com" className="w-full text-sm border border-slate-300 rounded-md px-3 py-2" />
        </Field>
        <Field label="Contractor Type">
          <select value={form.contractor_kind} onChange={(e) => setForm({ ...form, contractor_kind: e.target.value })} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white">
            <option value="owner_operator">Owner-Operator</option>
            <option value="vendor">Vendor / Service</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="TIN type">
          <select value={form.taxpayer_id_kind} onChange={(e) => setForm({ ...form, taxpayer_id_kind: e.target.value })} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white">
            <option value="ssn">SSN</option>
            <option value="ein">EIN</option>
          </select>
        </Field>
        <Field label="Taxpayer ID (optional — encrypted at rest)">
          <input value={form.taxpayer_id} onChange={(e) => setForm({ ...form, taxpayer_id: e.target.value })} data-testid="w9-manual-tin" placeholder="123-45-6789 or 12-3456789" className="w-full text-sm border border-slate-300 rounded-md px-3 py-2" />
        </Field>
        <Field label="Business Name (if different)">
          <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2" />
        </Field>
        <Field label="Address">
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2" />
        </Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="City"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2" /></Field>
          <Field label="State"><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2" /></Field>
          <Field label="Zip"><input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2" /></Field>
        </div>
        <Field label="Note (internal)">
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full text-sm border border-slate-300 rounded-md px-3 py-2" placeholder="e.g. Received via email Jan 15" />
        </Field>
        <Field label="W-9 file (PDF / JPG / PNG, ≤ 8MB) *">
          <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="w9-manual-file" className="w-full text-sm" />
        </Field>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="w9-manual-submit" className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] hover:bg-[#00247D] text-white rounded-md font-semibold text-sm disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? 'Uploading...' : 'Upload W-9'}
        </button>
      </div>
    </div>
  );
};

export default W9AdminPage;
